import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({
    title = 'Muthumuni Ayurveda Resorts — Authentic Beach & River Ayurveda in Beruwala & Aluthgama, Sri Lanka',
    description = 'Discover authentic Ayurveda wellness at Muthumuni Resorts: Beach Resort in Beruwala with oceanfront therapies, yoga & organic meals, and River Resort in Aluthgama offering peaceful riverside Panchakarma, detox & nature healing. Book your Sri Lanka Ayurveda retreat today.',
    keywords = 'Muthumuni Ayurveda, Ayurveda resort Beruwala, Ayurveda resort Aluthgama, Ayurveda Sri Lanka, Panchakarma Sri Lanka, beach Ayurveda resort Beruwala, river Ayurveda resort Aluthgama, authentic Ayurveda treatments, yoga retreat Sri Lanka, detox retreat Sri Lanka, wellness resort Bentota area, ayurvedic holiday Sri Lanka',
    image = '%PUBLIC_URL%/logo.png', // ← replace with better OG image (1200×630 recommended)
    url = 'https://muthumuniayurvedaresorts.com',
    pathname = '/',
}) => {
    const pageUrl = `${url}${pathname.startsWith('/') ? pathname : `/${pathname}`}`.replace(/\/+/g, '/');

    // More detailed structured data — added many recommended Hotel properties
    const structuredData = [
        {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Muthumuni Ayurveda Resorts",
            "url": "https://muthumuniayurvedaresorts.com",
            "logo": "https://muthumuniayurvedaresorts.com/logo.png",
            "sameAs": [
                "https://www.facebook.com/muthumuniayurveda",   // ← add real social profiles
                "https://www.instagram.com/muthumuniayurveda",
                "https://www.tripadvisor.com/..."               // add if exists
            ]
        },
        {
            "@context": "https://schema.org",
            "@type": "Hotel",
            "name": "Muthumuni Ayurveda Beach Resort Beruwala",
            "url": "https://muthumuniayurvedaresorts.com/beach/home",
            "description": "Authentic beachfront Ayurveda resort in Beruwala offering traditional Panchakarma, Abhyanga massage, yoga, herbal treatments, organic Sri Lankan cuisine and direct beach access.",
            "image": "https://muthumuniayurvedaresorts.com/images/beach-resort-hero.jpg",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Moragalla, Beach Side", // ← add real street if possible
                "addressLocality": "Beruwala",
                "addressRegion": "Western Province",
                "postalCode": "12060",                    // ← update if known
                "addressCountry": "LK"
            },
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": 6.4789,  // ← replace with real coordinates
                "longitude": 79.9821
            },
            "telephone": "+94 34 227 1234",             // ← add real phone
            "priceRange": "$$",                         // or "$$$"
            "starRating": {
                "@type": "Rating",
                "ratingValue": "4"                        // update real rating
            },
            "amenityFeature": [
                { "@type": "LocationFeatureSpecification", "name": "Beachfront" },
                { "@type": "LocationFeatureSpecification", "name": "Yoga Pavilion" },
                { "@type": "LocationFeatureSpecification", "name": "Ayurvedic Restaurant" },
                { "@type": "LocationFeatureSpecification", "name": "Free WiFi" }
            ]
        },
        {
            "@context": "https://schema.org",
            "@type": "Hotel",
            "name": "Muthumuni Ayurveda River Resort Aluthgama",
            "url": "https://muthumuniayurvedaresorts.com/river/home",
            "description": "Serene riverfront Ayurveda resort in Aluthgama featuring traditional detox programs, riverside yoga, nature therapies, Panchakarma and peaceful accommodation near Bentota.",
            "image": "https://muthumuniayurvedaresorts.com/images/river-resort-hero.jpg",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "River Side, Aluthgama", // ← add real street
                "addressLocality": "Aluthgama",
                "addressRegion": "Western Province",
                "postalCode": "12040",                    // ← update if known
                "addressCountry": "LK"
            },
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": 6.4340,  // ← replace with real
                "longitude": 79.9978
            },
            "telephone": "+94 34 227 5678",             // ← add real phone
            "priceRange": "$$",
            "starRating": {
                "@type": "Rating",
                "ratingValue": "4"
            },
            "amenityFeature": [
                { "@type": "LocationFeatureSpecification", "name": "River View" },
                { "@type": "LocationFeatureSpecification", "name": "Panchakarma Centre" },
                { "@type": "LocationFeatureSpecification", "name": "Organic Garden" },
                { "@type": "LocationFeatureSpecification", "name": "Free WiFi" }
            ]
        },
        {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "https://muthumuniayurvedaresorts.com",
            "name": "Muthumuni Ayurveda Resorts",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://muthumuniayurvedaresorts.com/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        }
    ];

    return (
        <Helmet>
            <html lang="en" />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content="Muthumuni Ayurveda Resorts" />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="googlebot" content="index, follow" />

            {/* Canonical – very important */}
            <link rel="canonical" href={pageUrl} />

            {/* Open Graph – better for social & WhatsApp sharing */}
            <meta property="og:locale" content="en_US" />
            <meta property="og:site_name" content="Muthumuni Ayurveda Resorts" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Muthumuni Ayurveda Beach & River Resorts Sri Lanka" />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:type" content="website" />

            {/* Twitter / X Cards */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content="@muthumuniayur" /> {/* ← add real handle if exists */}

            {/* JSON-LD – very powerful for rich results */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>
        </Helmet>
    );
};

export default SEO;